import { MealBenefitKind, UserDefinedRow } from "../../model";
import { EnumerationsLogic, Translations  } from "../../logic";
import { DatelyObject, Numeric, Years } from "../../util";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";

/** Client-side logic for MealBenefitUsecase  */
export class MealBenefitUsecaseLogic extends BaseUsecaseLogic {

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param row Row to update.
   * @param date The reference date for which the logic is done.
   * Typically the salary date of a calculation and the typically, the information needed is just the year for which the yearly changing numbers are fetched, but may later have more extended features.
   */
  public updateUsecase(row: UserDefinedRow, date: DatelyObject): void {
    this.setValues(row.data?.kind, row, date);
  }

  /** Gets validation message for MealBenefitUsecase  */
  public getUcValidation(row: UserDefinedRow): UsecaseValidationMessage {

    row.data = row.data ??  {};
    row.data.isTaxValue = row.data.isTaxValue ?? false;
    row.data.isTaxValueDeductionDone = row.data.isTaxValueDeductionDone ?? false;
    switch (row.data.kind) {
      case MealBenefitKind.MealAllowance:
        return {
          type: "default",
          msg: `${EnumerationsLogic.getEnumLabel("MealBenefitKind", row.data.kind)}, ${Translations.get("SALAXY.VALIDATION.Usecases.mealBenefit.allowance")} ${Numeric.formatPrice(row.price * (row.count ?? 1))}.`,
        };
      case MealBenefitKind.CateringContract:
      case MealBenefitKind.Institute:
      case MealBenefitKind.MealTicket:
      case MealBenefitKind.RestaurantWorker:
      case MealBenefitKind.Teacher:
      case MealBenefitKind.TaxableAmount:
      case MealBenefitKind.CollectedReimbursement:
        if (row.data.isTaxValue) {
          return {
            type: "default",
            msg: `${EnumerationsLogic.getEnumLabel("MealBenefitKind", row.data.kind)}, ${Translations.get("SALAXY.VALIDATION.Usecases.mealBenefit.default")} ${Numeric.formatPrice((row.data.taxablePrice ?? 0) * (row.count ?? 1))}, ${Translations.get("SALAXY.VALIDATION.Usecases.mealBenefit.taxValueDeduction")}.`,
          };
        }
        else
        {
          return {
            type: "default",
            msg: `${EnumerationsLogic.getEnumLabel("MealBenefitKind", row.data.kind)}, ${Translations.get("SALAXY.VALIDATION.Usecases.mealBenefit.default")} ${Numeric.formatPrice((row.data.taxablePrice ?? 0) * (row.count ?? 1))}${row.data.deduction ? ", " + Translations.get("SALAXY.VALIDATION.Usecases.mealBenefit.deduction") + " "  + Numeric.formatPrice(row.data.deduction) +"." : ""}`,
          };
        }
      case MealBenefitKind.Undefined:
      default:
        if (row.data) {
          row.data.taxablePrice = row.price;
        }
        return {
          type: "error",
          msg: "SALAXY.VALIDATION.Usecases.mealBenefit.undefined",
        };
    }
  }

   private setValues(kind: MealBenefitKind, row: UserDefinedRow, date: DatelyObject): void {
    const sideCosts = Years.getYearlyChangingNumbers(date).sideCosts;
    row.data = row.data ??  {};
    const data = row.data;
    switch (kind) {
      case MealBenefitKind.MealAllowance:
        row.price = Math.min(row.price, sideCosts.taxFreeMealAllowance);
        data.taxablePrice = row.price;
        data.deduction = null;
        data.isTaxValue = false;
        data.isTaxValueDeductionDone = false;
        break;
      case MealBenefitKind.CateringContract:
        row.price = sideCosts.mealBenefitCateringContract; // 6.80;
        data.taxablePrice = row.price;
        break;
      case MealBenefitKind.Institute:
        row.price = sideCosts.mealBenefitInstitute; // 5.10;
        data.taxablePrice = row.price;
        break;
      case MealBenefitKind.MealTicket:
        if (!row.price) {
          row.price = 0;
        }
        else if (row.price < sideCosts.mealTicketLowerLimit)
        {
            row.price = sideCosts.mealTicketLowerLimit;
        }
        else if (row.price > sideCosts.mealTicketUpperLimit)
        {
          row.price = sideCosts.mealTicketUpperLimit;
        }
        else
        {
          // row.price = row.price;
        }
        data.taxablePrice = sideCosts.mealTicketTaxable * row.price;
        break;
      case MealBenefitKind.RestaurantWorker:
        row.price = sideCosts.mealBenefitRestaurantWorker; // 5.78;
        data.taxablePrice = row.price;
        break;
      case MealBenefitKind.Teacher:
        row.price = sideCosts.mealBenefitTeacher; // 4.08;
        data.taxablePrice = row.price;
        break;
      case MealBenefitKind.CollectedReimbursement:
        data.isTaxValueDeductionDone = false;
        data.isTaxValue = true;
        data.deduction = null;
        data.taxablePrice = row.price;
        break;
      case MealBenefitKind.TaxableAmount:
      case MealBenefitKind.Undefined:
      default:
        data.taxablePrice = row.price;
    }

    if (kind != MealBenefitKind.MealAllowance) {
      if (data.isTaxValue)
      {
          data.deduction = null;
      }
    }
    data.isTaxValue = data.isTaxValue ?? false;
    data.isTaxValueDeductionDone = data.isTaxValueDeductionDone ?? false;
  }
}
